import React from 'react';
import { Alert, Button, Modal, Spinner } from 'react-bootstrap';
import csvtojson from 'csvtojson';
import { Show } from 'Layout';
import { PageStatus } from 'enums';
import { AuthAPI } from "../../API";
import { referralModalDict } from 'Languages/ReferralTranslations';
import { withTranslation, Trans } from 'react-i18next';
import { checkNumberValidation, checkEmailValidation } from "../../Helper/Validations";
import country from 'config/countryConfig';
import { PORTAL_CODE } from '../../config/portalBranding';

// Japan portal drops the Mobile field from the referral flow (same as the
// JP profile/update-profile forms). Other portals keep it.
const IS_JAPAN_PORTAL = PORTAL_CODE === 'JP';

export type FormValue = {
    "name": string,
};


type State = {
    status: string,
    error: string | null,
    country: any,
    name: string,
};

class Form extends React.Component<any, any> {
    constructor(props) {
        super(props);
        this.state = {
            status: PageStatus.None,
            error: null,
            isActive: 1,
            points: 0,
            mode: "",
            importedData: { users: [] },
            name: "",
            email: "",
            referralStatus: "Invited",
            referralMethod: "Manual",
            phoneNumber: "",
            remainingReferrals: 0,
        };
    }


    formValues() {
        return {
            name: this.state.name,
            email: this.state.email,
            userId: this.props.userId,
            referralStatus: "Invited",
            referralMethod: "Manual",
            // JP: the Mobile field is not rendered, so send an empty string
            // (key present, value empty) — the same pattern the JP basic-
            // profile form uses to satisfy backend validators.
            phoneNumber: IS_JAPAN_PORTAL ? '' : this.state.phoneNumber
        };
    }

    checkPhoneNumberValidation(e) {
        if (checkNumberValidation(e)) { return }
        if (e.target.value.length <= 10) { this.setState({ phoneNumber: e.target.value }); }
    }

    checkRemainingReferrals() {
        return Promise.resolve()
            .then(() => this.setState({ status: PageStatus.Submitting }))
            .then(() => AuthAPI.getRemainingReferalCount({ userId: this.props.userId }))
            .then((response) => {
                this.setState({ remainingReferrals: response });
            })
            .catch((error) => {
                this.setState({ status: PageStatus.Error, error: error.message });
            });
    }

    create() {
        if (this.state.remainingReferrals == 0) {
            alert(referralModalDict[this.props.language]["No more referrals"] || "You have exceeded the limit of referrals within 30 days.");
            return;
        }
        if (1 > this.state.remainingReferrals) {
            alert(this.props.t('referrals.maxReferralsWarning', { count: this.state.remainingReferrals }));
            return;
        }
        const valuesIn = this.formValues()
        return Promise.resolve()
            .then(() => this.setState({ status: PageStatus.Submitting }))
            .then(() => AuthAPI.createReferralRequest(valuesIn, this.props.language))
            .then((country) => {
                this.props.onSubmit(this.props.userId);
                this.checkRemainingReferrals();
                return this.setState({ status: PageStatus.Submitted });
            })
            .catch((error) => {
                let newError;
                if (error.message === "You have already send invite to this email") {
                    newError = referralModalDict[this.props.language]["Already email sent"] || "You have already send invite to this email";
                } else if (error.message === "This email is already registered") {
                    newError = this.props.t('referrals.emailAlreadyRegistered');
                } else { newError = error.message; }
                this.setState({ status: PageStatus.Error, error: newError });
            });
    }

    createBulk() {
        if (this.state.remainingReferrals == 0) {
            alert(referralModalDict[this.props.language]["No more referrals"] || "You have exceeded the limit of referrals within 30 days.");
            return;
        }
        if (this.state.importedData.users.length > this.state.remainingReferrals) {
            alert(this.props.t('referrals.maxReferralsWarning', { count: this.state.remainingReferrals }));
            return;
        }
        return Promise.resolve()
            .then(() => this.setState({ status: PageStatus.Submitting }))
            .then(() => AuthAPI.createBulkReferralRequest(this.state.importedData, this.props.language))
            .then((country) => {
                this.props.onSubmit(this.props.userId);
                this.checkRemainingReferrals();
                return this.setState({ status: PageStatus.Submitted });
            })
            .catch((error) => {
                this.setState({ status: PageStatus.Error, error: error.message });
            });
    }

    downloadFile = () => {
        const downloadUrl = `${process.env.REACT_APP_BASE_URL_API}/Images/Sample.csv`;
        window.open(downloadUrl, '_blank');
    };

    handleFileChange = async (event) => {
        const file = event.target.files[0];
        if (!file) { this.setState({ importedData: { users: [] } }); return; }
        try {
            const jsonData = await this.convertCsvToJson(file);
            let transformedData = jsonData.map(this.modifiedConvertedJson);
            const validateRow = (row, index) => {
                const rowNum = index + 2;
                // JP: mobile is not collected on the referral flow, so the
                // CSV is not required to carry a Mobile column.
                if (!IS_JAPAN_PORTAL && (!row.phoneNumber || row.phoneNumber.length !== 10)) {
                    alert(this.props.t('referrals.invalidMobileInRow', { rowNum }));
                    return false;
                }
                if (!row.name) {
                    alert(this.props.t('referrals.invalidNameInRow', { rowNum }));
                    return false;
                }
                if (!row.email) {
                    alert(this.props.t('referrals.invalidEmailInRow', { rowNum }));
                    return false;
                }
                return true;
            };
            const invalidIndex = transformedData.findIndex((row, index) => !validateRow(row, index));
            if (invalidIndex !== -1) { transformedData = []; }
            this.setState({ importedData: { users: transformedData } });
        } catch (error) {
            console.error('Error converting CSV to JSON:', error);
        }
    };

    convertCsvToJson = (file: File): Promise<any[]> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = () => {
                const csvContent: any = reader.result

                if (!csvContent.trim()) {
                    reject(new Error('CSV content is empty.'));
                    return;
                }

                csvtojson()
                    .fromString(csvContent)
                    .then(jsonData => resolve(jsonData))
            };

            reader.onerror = (event) => {
                const message = reader.error?.message ?? 'Unknown error during file reading';
                reject(new Error(message));
            };

            reader.readAsText(file);
        });
    };

    modifiedConvertedJson = (obj) => {
        return {
            name: obj.Name,
            email: obj.Email,
            // JP: Mobile column is optional on the CSV — default to '' so
            // the key is present but empty (matches the single-referral
            // JP payload and keeps the backend validator happy).
            phoneNumber: IS_JAPAN_PORTAL ? (obj.Mobile || '') : obj.Mobile,
            userId: this.props.userId,
            referralStatus: "Invited",
            referralMethod: "File",
        };
    }

    componentDidMount(): void { this.checkRemainingReferrals(); }

    render() {
        const lang = this.props.language ?? 'en';
        const { t } = this.props;
        const remainingReferralsCss = () => {
            if (this.state.remainingReferrals < 4) { return "text-danger"; }
            else if (this.state.remainingReferrals < 6) { return "text-warning"; }
            else { return "text-success" }
        }
        return (
            <Modal
                centered
                size="xl"
                backdrop="static"
                onHide={this.props.onClose}
                show={this.props.show}
                style={{ zIndex: 1201 }}
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        {t('referrals.referral')}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '78vh', overflow: 'auto' }}>
                    <Show when={this.state.status === PageStatus.Loading}>
                        <div className="d-flex justify-content-center w-100 p-5">
                            <Spinner animation="border" variant="primary" />
                        </div>
                    </Show>

                    <div className="jumbotron bg-white p-3 border shadow-sm">
                        <div className='d-flex justify-content-between'>
                            <div><b>{t('referrals.referAFriend')}</b></div>
                            <div><b>{t('referrals.modal.remainingReferrals')} </b> <b className={remainingReferralsCss()}>{this.state.remainingReferrals}</b></div>
                        </div>
                        <hr />
                        <br />
                        <p>
                            <Trans
                                i18nKey="referrals.modal.emailIntro"
                                values={{
                                    maxPoints: String(country.referralMonthlyMaxPoints),
                                    pointsLabel: country.pointsLabel,
                                    brand: country.brandName,
                                    referralUrl: `${window.location.origin}/#/referrals/view/${this.props.userId}`,
                                }}
                                components={[
                                    <span />,
                                    // eslint-disable-next-line jsx-a11y/anchor-has-content
                                    <a href={`${window.location.origin}/#/referrals/view/${this.props.userId}`}>{' '}</a>,
                                    <b />,
                                ]}
                            />
                        </p>
                        <br />
                        <div>
                            <div className="row">
                                <div className="col">
                                    <label htmlFor="title">{t('common.columnName')}*</label>
                                    <input
                                        className="form-control"
                                        name="name"
                                        onChange={(e) => this.setState({ name: e.target.value })}
                                        value={this.state.name}
                                        placeholder={t('common.enterHerePlaceholder')}
                                        title={t('common.enterHerePlaceholder')}
                                        required
                                    />
                                </div>
                                <div className="col">
                                    <label htmlFor="email">{t('common.emailLabel')}*</label>
                                    <input
                                        className="form-control"
                                        id="email"
                                        name="email"
                                        onChange={(e) => this.setState({ email: e.target.value })}
                                        value={this.state.email}
                                        title={t('common.enterHerePlaceholder')}
                                        placeholder={t('common.enterHerePlaceholder')}
                                    />
                                </div>
                                {/* Mobile field hidden for JP — NipponPolls referral flow does not collect mobile. */}
                                {!IS_JAPAN_PORTAL && (
                                <div className="col">
                                    <label htmlFor="Mobile">{t('common.mobileLabel')}*</label>
                                    <input
                                        type='text'
                                        className="form-control"
                                        id="phoneNumber"
                                        name="phoneNumber"
                                        onChange={(e) => this.checkPhoneNumberValidation(e, 'sendMessage')}
                                        value={this.state.phoneNumber}
                                        title={t('common.enterHerePlaceholder')}
                                        placeholder={t('common.enterHerePlaceholder')}
                                    />
                                </div>
                                )}
                            </div>
                            <div className='d-flex flex-row align-items-center mt-3'>
                                <Button
                                    onClick={() => this.create()}
                                    variant="primary"
                                    disabled={!this.state.name || !this.state.email || !checkEmailValidation({ "target": { "value": this.state.email } })}
                                    className=""
                                    size="sm"
                                >
                                    {t('referrals.modal.referViaEmail')}
                                </Button>
                            </div>
                            <div className='mt-3'>
                                <Alert variant="danger" show={this.state.status === PageStatus.Error}>
                                    {this.state.error}
                                </Alert>
                            </div>
                        </div>
                    </div>

                    {/* <div className="jumbotron bg-white p-3 border shadow-sm">
                        <div><b>{t('referrals.uploadFromCsvList')}</b></div>
                        <hr />
                        <br />
                        <div>
                            {referralModalDict[lang]["CSV Description"] || "CSV Description"}
                            <button onClick={this.downloadFile} className="link-display bg-transparent" title='Download sample file' style={{ color: 'orange', cursor: 'pointer' }}> {referralModalDict[lang]["Download"] || "Download"} </button>
                            <div>
                                <input className="mt-1" type="file" accept=".csv"
                                    onChange={this.state.remainingReferrals > 0 ? (e) => this.handleFileChange(e) : null}
                                    disabled={this.state.remainingReferrals < 1}
                                />
                            </div>
                            <div>
                                <Button
                                    onClick={() => this.createBulk()}
                                    variant="primary"
                                    disabled={this.state.importedData.users.length === 0}
                                    className="mt-3"
                                    size="sm"
                                >
                                    {referralModalDict[lang]["Import"] || "Import"}
                                </Button>
                            </div>
                        </div>
                    </div> */}
                </Modal.Body>
            </Modal>
        );
    }
}

const FormWithTranslation = withTranslation()(Form);

export { FormWithTranslation as Form };
